import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Light theme configuration
  const lightTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#0a0d14',
        dark: '#05070c',
        light: '#1c2433',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#5168ff',
        dark: '#3448d9',
        light: '#8ea0ff',
      },
      background: {
        default: '#f8fbff',
        paper: 'rgba(255, 255, 255, 0.74)',
      },
      text: {
        primary: '#080d18',
        secondary: '#5f6675',
      },
      divider: 'rgba(23, 31, 48, 0.1)',
      // Custom colors for our components
      custom: {
        heroGradient: 'radial-gradient(circle at 85% 25%, rgba(98, 111, 255, 0.25), transparent 34%), radial-gradient(circle at 15% 70%, rgba(112, 209, 255, 0.22), transparent 34%), linear-gradient(180deg, #ffffff 0%, #f4f8ff 55%, #eef7ff 100%)',
        cardBg: 'rgba(255, 255, 255, 0.72)',
        cardBorder: 'rgba(21, 31, 54, 0.1)',
        cardHover: 'rgba(255, 255, 255, 0.9)',
        accent: '#5168ff',
        blueMist: '#dcecff',
        lavenderMist: '#ebe7ff',
        cyanMist: '#e1f8ff',
        success: '#15905f',
        warning: '#b7791f',
        error: '#c24150',
      }
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
      h1: { letterSpacing: 0 },
      h2: { letterSpacing: 0 },
      h3: { letterSpacing: 0 },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '999px',
            fontWeight: 700,
            boxShadow: 'none',
            minHeight: 38,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: '22px',
            border: '1px solid rgba(21, 31, 54, 0.1)',
            backdropFilter: 'blur(18px)',
            boxShadow: '0 18px 70px rgba(82, 107, 170, 0.12)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.74)',
            borderRadius: '22px',
            border: '1px solid rgba(21, 31, 54, 0.1)',
            backdropFilter: 'blur(18px)',
            boxShadow: '0 18px 70px rgba(82, 107, 170, 0.12)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '999px',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            border: '1px solid rgba(21, 31, 54, 0.1)',
            fontWeight: 650,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: '22px',
            border: '1px solid rgba(21, 31, 54, 0.1)',
            backgroundColor: 'rgba(255, 255, 255, 0.74)',
            backdropFilter: 'blur(18px)',
            boxShadow: '0 18px 70px rgba(82, 107, 170, 0.12)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: '#5f6675',
            fontSize: '0.78rem',
            fontWeight: 800,
            textTransform: 'uppercase',
          },
          root: {
            borderBottom: '1px solid rgba(21, 31, 54, 0.08)',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.74)',
            border: '1px solid rgba(21, 31, 54, 0.1)',
            borderRadius: '18px !important',
            boxShadow: '0 18px 70px rgba(82, 107, 170, 0.1)',
            overflow: 'hidden',
            '&:before': {
              display: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: 'rgba(255, 255, 255, 0.62)',
            '& fieldset': {
              borderColor: 'rgba(21, 31, 54, 0.12)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(21, 31, 54, 0.24)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#5168ff',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            backgroundColor: 'rgba(81, 104, 255, 0.1)',
          },
          bar: {
            borderRadius: 999,
            background: 'linear-gradient(90deg, #5168ff, #73d5ff)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            border: '1px solid rgba(21, 31, 54, 0.1)',
            borderRadius: 24,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background:
              'radial-gradient(circle at 92% 8%, rgba(91, 109, 255, 0.22), transparent 30%), radial-gradient(circle at 8% 72%, rgba(112, 209, 255, 0.2), transparent 32%), linear-gradient(180deg, #ffffff 0%, #f6f9ff 54%, #edf7ff 100%)',
            backgroundAttachment: 'fixed',
          },
        },
      },
    },
  });

  // Dark theme configuration
  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#f8fafc',
        dark: '#e2e8f0',
        light: '#ffffff',
        contrastText: '#0b0f19',
      },
      secondary: {
        main: '#8ea0ff',
        dark: '#6d80ef',
        light: '#c7d1ff',
      },
      background: {
        default: '#070a12',
        paper: 'rgba(13, 18, 31, 0.76)',
      },
      text: {
        primary: '#f8fafc',
        secondary: '#b7bfce',
      },
      divider: 'rgba(226, 232, 240, 0.12)',
      // Custom colors for our components
      custom: {
        heroGradient: 'radial-gradient(circle at 85% 25%, rgba(97, 111, 255, 0.28), transparent 35%), radial-gradient(circle at 14% 74%, rgba(72, 202, 255, 0.18), transparent 34%), linear-gradient(180deg, #070a12 0%, #0b1020 100%)',
        cardBg: 'rgba(13, 18, 31, 0.76)',
        cardBorder: 'rgba(226, 232, 240, 0.12)',
        cardHover: 'rgba(20, 29, 48, 0.9)',
        accent: '#8ea0ff',
        blueMist: '#23345f',
        lavenderMist: '#332c63',
        cyanMist: '#173846',
        success: '#22c55e',
        warning: '#f59e0b',
        error: '#ef4444',
      }
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
      h1: { letterSpacing: 0 },
      h2: { letterSpacing: 0 },
      h3: { letterSpacing: 0 },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '999px',
            fontWeight: 700,
            boxShadow: 'none',
            minHeight: 38,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: '22px',
            border: '1px solid rgba(226, 232, 240, 0.12)',
            backdropFilter: 'blur(18px)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.24)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'rgba(13, 18, 31, 0.76)',
            borderRadius: '22px',
            border: '1px solid rgba(226, 232, 240, 0.12)',
            backdropFilter: 'blur(18px)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.24)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '999px',
            backgroundColor: 'rgba(13, 18, 31, 0.72)',
            border: '1px solid rgba(226, 232, 240, 0.12)',
            fontWeight: 650,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: '22px',
            border: '1px solid rgba(226, 232, 240, 0.12)',
            backgroundColor: 'rgba(13, 18, 31, 0.76)',
            backdropFilter: 'blur(18px)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.24)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: '#b7bfce',
            fontSize: '0.78rem',
            fontWeight: 800,
            textTransform: 'uppercase',
          },
          root: {
            borderBottom: '1px solid rgba(226, 232, 240, 0.1)',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'rgba(13, 18, 31, 0.76)',
            border: '1px solid rgba(226, 232, 240, 0.12)',
            borderRadius: '18px !important',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.24)',
            overflow: 'hidden',
            '&:before': {
              display: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: 'rgba(13, 18, 31, 0.58)',
            '& fieldset': {
              borderColor: 'rgba(226, 232, 240, 0.14)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(226, 232, 240, 0.24)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#8ea0ff',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            backgroundColor: 'rgba(142, 160, 255, 0.14)',
          },
          bar: {
            borderRadius: 999,
            background: 'linear-gradient(90deg, #8ea0ff, #73d5ff)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            border: '1px solid rgba(226, 232, 240, 0.12)',
            borderRadius: 24,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background:
              'radial-gradient(circle at 90% 8%, rgba(97, 111, 255, 0.26), transparent 30%), radial-gradient(circle at 10% 76%, rgba(72, 202, 255, 0.16), transparent 32%), linear-gradient(180deg, #070a12 0%, #0b1020 100%)',
            backgroundAttachment: 'fixed',
          },
        },
      },
    },
  });

  const theme = isDarkMode ? darkTheme : lightTheme;

  const value = {
    isDarkMode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
